import React, { useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon,
  Save as SaveIcon
} from '@mui/icons-material';

// dnd-kit
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SessionSchedules({ session, competencia, onUpdateSession, allUsedIds }) {
  // Selección múltiple de pruebas (en lugar de selectedEvent string)
  const [selectedEvents, setSelectedEvents] = useState([]);

  // Filas seleccionadas de la tabla
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Edición masiva
  const [massEditing, setMassEditing] = useState(false);
  const [massEditValues, setMassEditValues] = useState({});

  // Edición individual
  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState({});

  // Diálogos de confirmación
  const [openConfirmDeleteOne, setOpenConfirmDeleteOne] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const [openConfirmDeleteMany, setOpenConfirmDeleteMany] = useState(false);

  // Si no hay session, no renderizamos
  if (!session) return null;

  // schedules de la sesión
  const schedules = session.schedules || [];

  // Actualizar los schedules en la sesión
  const updateSchedules = (newSchedules) => {
    onUpdateSession({ ...session, schedules: newSchedules });
    // Limpiamos estados de selección/edición
    setSelectedRows(new Set());
    setMassEditing(false);
    setMassEditValues({});
    setEditingId(null);
    setEditingValues({});
  };

  // Construir lista de pruebas combinadas
  const getCombinedEventsList = () => {
    const combinedEvents = [];
    competencia?.pruebas?.forEach((prueba) => {
      // Varonil
      prueba.categoriaIdsVaronil?.forEach((catId) => {
        const categoria = competencia.categorias.find((cat) => cat.id === catId);
        if (categoria) {
          combinedEvents.push({
            id: `${prueba.id}-${catId}-V`,
            eventId: prueba.id,
            categoryId: catId,
            nombre: prueba.nombre,
            testType: prueba.testType,
            rama: 'Varonil',
            categoria: categoria.nombre,
            eventCode: prueba.eventCode
          });
        }
      });
      // Femenil
      prueba.categoriaIdsFemenil?.forEach((catId) => {
        const categoria = competencia.categorias.find((cat) => cat.id === catId);
        if (categoria) {
          combinedEvents.push({
            id: `${prueba.id}-${catId}-F`,
            eventId: prueba.id,
            categoryId: catId,
            nombre: prueba.nombre,
            testType: prueba.testType,
            rama: 'Femenil',
            categoria: categoria.nombre,
            eventCode: prueba.eventCode
          });
        }
      });
    });
    return combinedEvents;
  };

  // Agregar *varias* pruebas a la vez
  const handleAddSchedules = () => {
    if (!selectedEvents || selectedEvents.length === 0) return;

    const evtList = getCombinedEventsList();
    const newSchedules = [...schedules];

    selectedEvents.forEach((evtId) => {
      const evt = evtList.find((x) => x.id === evtId);
      if (evt) {
        // Chequeamos si ya existe en newSchedules
        const alreadyUsed = newSchedules.some((sch) => sch.combinedEventId === evtId);
        if (!alreadyUsed) {
          newSchedules.push({
            id: Date.now().toString() + Math.random().toString().slice(2),
            combinedEventId: evt.id,
            hora: '00:00',
            evento: newSchedules.length + 1,
            testType: evt.testType,
            prueba: evt.nombre,
            categoria: evt.categoria,
            rama: evt.rama,
            fase: 'Final',
            heats: 1,
            inscritos: 0,
            especificaciones: '',
            eventCode: evt.eventCode
          });
        }
      }
    });

    updateSchedules(newSchedules);
    setSelectedEvents([]);
  };

  // Drag & drop
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = schedules.findIndex((item) => item.id === active.id);
      const newIndex = schedules.findIndex((item) => item.id === over.id);
      const reordered = arrayMove(schedules, oldIndex, newIndex);
      updateSchedules(reordered);
    }
  };

  // Seleccionar/deseleccionar fila
  const handleSelectRow = (id, checked) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (checked) newSet.add(id);
      else newSet.delete(id);
      return newSet;
    });
  };
  const allSelected = schedules.length > 0 && selectedRows.size === schedules.length;

  // Eliminar varias
  const handleDeleteMany = () => {
    setOpenConfirmDeleteMany(true);
  };
  const handleConfirmDeleteMany = () => {
    const newSchedules = schedules.filter((item) => !selectedRows.has(item.id));
    updateSchedules(newSchedules);
    setOpenConfirmDeleteMany(false);
  };

  // Edición masiva
  const handleEditMany = () => {
    setMassEditing(true);
    setEditingId(null);
    const newValues = {};
    schedules.forEach((row) => {
      if (selectedRows.has(row.id)) {
        newValues[row.id] = {
          hora: row.hora,
          evento: row.evento,
          fase: row.fase,
          especificaciones: row.especificaciones || ''
        };
      }
    });
    setMassEditValues(newValues);
  };
  const handleCancelMassEdit = () => {
    setMassEditing(false);
    setMassEditValues({});
  };
  const handleSaveMassEdit = () => {
    const updated = schedules.map((row) => {
      if (selectedRows.has(row.id)) {
        const changes = massEditValues[row.id] || {};
        return {
          ...row,
          hora: changes.hora,
          evento: changes.evento,
          fase: changes.fase,
          especificaciones: changes.especificaciones
        };
      }
      return row;
    });
    updateSchedules(updated);
  };

  // Eliminar individual
  const handleDeleteOne = (row) => {
    setRowToDelete(row);
    setOpenConfirmDeleteOne(true);
  };
  const handleConfirmDeleteOne = () => {
    if (!rowToDelete) return;
    const newSchedules = schedules.filter((item) => item.id !== rowToDelete.id);
    updateSchedules(newSchedules);
    setRowToDelete(null);
    setOpenConfirmDeleteOne(false);
  };

  // Edición individual
  const handleEditRow = (row) => {
    if (massEditing) return;
    setEditingId(row.id);
    setEditingValues({ ...row });
  };
  const handleSaveRow = (id) => {
    const updated = schedules.map((row) =>
      row.id === id ? { ...row, ...editingValues } : row
    );
    updateSchedules(updated);
    setEditingId(null);
    setEditingValues({});
  };
  const handleCancelEditRow = () => {
    setEditingId(null);
    setEditingValues({});
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Dialogos de confirmación para eliminar */}
      <Dialog open={openConfirmDeleteOne} onClose={() => setOpenConfirmDeleteOne(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar este horario?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDeleteOne(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeleteOne}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openConfirmDeleteMany} onClose={() => setOpenConfirmDeleteMany(false)}>
        <DialogTitle>Eliminar Seleccionados</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se eliminarán {selectedRows.size} horarios seleccionados. ¿Deseas continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDeleteMany(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeleteMany}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Barra para agregar *múltiples* pruebas */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={9}>
            <FormControl fullWidth>
              <InputLabel>Prueba</InputLabel>
              <Select
                // Permitir selección múltiple
                multiple
                value={selectedEvents}
                onChange={(e) => setSelectedEvents(e.target.value)}
                label="Prueba"
                // Muestra los valores seleccionados separados por coma
                renderValue={(selected) => selected.join(', ')}
              >
                {getCombinedEventsList().map((evt) => {
                  const isGloballyUsed = allUsedIds.has(evt.id);
                  const isUsedInThisSession = schedules.some(
                    (sch) => sch.combinedEventId === evt.id
                  );
                  const isDisabled = isGloballyUsed || isUsedInThisSession;
                  return (
                    <MenuItem key={evt.id} value={evt.id} disabled={isDisabled}>
                      {`${evt.nombre} ${evt.testType} ${evt.categoria} (${evt.rama})`}
                      {isDisabled ? ' (Ya agregado en otra sesión)' : ''}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddSchedules}
              fullWidth
            >
              Agregar Horario
            </Button>
          </Grid>
        </Grid>

        {/* Botones de borrar/editar masivo */}
        {!massEditing && selectedRows.size > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button variant="outlined" color="error" onClick={handleDeleteMany}>
              Borrar seleccionados ({selectedRows.size})
            </Button>
            <Button variant="outlined" color="primary" onClick={handleEditMany}>
              Editar seleccionados ({selectedRows.size})
            </Button>
          </Box>
        )}

        {/* Botones de Guardar/Cancelar edición masiva */}
        {massEditing && (
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button variant="contained" color="success" onClick={handleSaveMassEdit}>
              <SaveIcon sx={{ mr: 1 }} />
              Guardar cambios
            </Button>
            <Button variant="outlined" onClick={handleCancelMassEdit}>
              Cancelar
            </Button>
          </Box>
        )}
      </Paper>

      {/* Tabla con drag & drop */}
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={schedules.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Mover</TableCell>
                  <TableCell>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={
                        selectedRows.size > 0 && selectedRows.size < schedules.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Seleccionar todos
                          setSelectedRows(new Set(schedules.map((row) => row.id)));
                        } else {
                          // Deseleccionar todos
                          setSelectedRows(new Set());
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>Hora</TableCell>
                  <TableCell>#Evento</TableCell>
                  <TableCell>Prueba</TableCell>
                  <TableCell>Categoría</TableCell>
                  <TableCell>Rama</TableCell>

                  {/* Agregamos la columna Esp entre Rama y Fase */}
                  <TableCell>Esp</TableCell>

                  <TableCell>Fase</TableCell>
                  <TableCell>Heats</TableCell>
                  <TableCell>Inscritos</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((row) => (
                  <SortableRow
                    key={row.id}
                    row={row}
                    massEditing={massEditing}
                    massEditValues={massEditValues}
                    setMassEditValues={setMassEditValues}
                    editingId={editingId}
                    editingValues={editingValues}
                    setEditingValues={setEditingValues}
                    handleSaveRow={handleSaveRow}
                    handleCancelEditRow={handleCancelEditRow}
                    handleEditRow={handleEditRow}
                    selected={selectedRows.has(row.id)}
                    handleSelectRow={handleSelectRow}
                    onDeleteOne={handleDeleteOne}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SortableContext>
      </DndContext>
    </Box>
  );
}

/**
 * SortableRow:
 * - Si massEditing && la fila está seleccionada => edición masiva inline.
 * - Si editingId === row.id => edición individual inline.
 * - Sino => solo lectura.
 */
function SortableRow({
  row,
  massEditing,
  massEditValues,
  setMassEditValues,
  editingId,
  editingValues,
  setEditingValues,
  handleSaveRow,
  handleCancelEditRow,
  handleEditRow,
  selected,
  handleSelectRow,
  onDeleteOne
}) {
  const { setNodeRef, transform, transition, isDragging, attributes, listeners } =
    useSortable({ id: row.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.5 } : {})
  };

  const isEditingIndividually = editingId === row.id;
  const isInMassEdit = massEditing && selected;

  // Inicializamos massEditValues si la fila está en edición masiva
  if (isInMassEdit && !massEditValues[row.id]) {
    massEditValues[row.id] = {
      hora: row.hora,
      evento: row.evento,
      fase: row.fase,
      especificaciones: row.especificaciones || ''
    };
  }

  // Handlers para edición masiva
  const handleMassChange = (field, value) => {
    setMassEditValues((prev) => ({
      ...prev,
      [row.id]: {
        ...prev[row.id],
        [field]: value
      }
    }));
  };

  // Handlers para edición individual
  const handleIndivChange = (field, value) => {
    setEditingValues((prev) => ({ ...prev, [field]: value }));
  };

  // Render celdas
  const renderHora = () => {
    if (isInMassEdit) {
      return (
        <TextField
          type="time"
          value={massEditValues[row.id]?.hora || '00:00'}
          onChange={(e) => handleMassChange('hora', e.target.value)}
          size="small"
        />
      );
    } else if (isEditingIndividually) {
      return (
        <TextField
          type="time"
          value={editingValues.hora || '00:00'}
          onChange={(e) => handleIndivChange('hora', e.target.value)}
          size="small"
        />
      );
    } else {
      return row.hora;
    }
  };

  const renderEvento = () => {
    if (isInMassEdit) {
      return (
        <TextField
          type="number"
          value={massEditValues[row.id]?.evento || ''}
          onChange={(e) => handleMassChange('evento', e.target.value)}
          size="small"
        />
      );
    } else if (isEditingIndividually) {
      return (
        <TextField
          type="number"
          value={editingValues.evento || ''}
          onChange={(e) => handleIndivChange('evento', e.target.value)}
          size="small"
        />
      );
    } else {
      return row.evento;
    }
  };

  const renderFase = () => {
    if (isInMassEdit) {
      return (
        <FormControl size="small">
          <InputLabel>Fase</InputLabel>
          <Select
            value={massEditValues[row.id]?.fase || 'Final'}
            label="Fase"
            onChange={(e) => handleMassChange('fase', e.target.value)}
          >
            <MenuItem value="Final">Final</MenuItem>
            <MenuItem value="Semifinal">Semifinal</MenuItem>
            <MenuItem value="Eliminatoria">Eliminatoria</MenuItem>
          </Select>
        </FormControl>
      );
    } else if (isEditingIndividually) {
      return (
        <FormControl size="small">
          <InputLabel>Fase</InputLabel>
          <Select
            value={editingValues.fase || 'Final'}
            label="Fase"
            onChange={(e) => handleIndivChange('fase', e.target.value)}
          >
            <MenuItem value="Final">Final</MenuItem>
            <MenuItem value="Semifinal">Semifinal</MenuItem>
            <MenuItem value="Eliminatoria">Eliminatoria</MenuItem>
          </Select>
        </FormControl>
      );
    } else {
      return row.fase;
    }
  };

  // Nueva columna "Esp" (especificaciones)
  const renderEspecificaciones = () => {
    if (isInMassEdit) {
      return (
        <TextField
          value={massEditValues[row.id]?.especificaciones || ''}
          onChange={(e) => handleMassChange('especificaciones', e.target.value)}
          size="small"
        />
      );
    } else if (isEditingIndividually) {
      return (
        <TextField
          value={editingValues.especificaciones || ''}
          onChange={(e) => handleIndivChange('especificaciones', e.target.value)}
          size="small"
        />
      );
    } else {
      return row.especificaciones || '';
    }
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      {/* Ícono para arrastrar */}
      <TableCell>
        <IconButton {...listeners} {...attributes}>
          <DragIndicatorIcon />
        </IconButton>
      </TableCell>

      {/* Checkbox de selección */}
      <TableCell>
        <Checkbox
          checked={selected}
          onChange={(e) => handleSelectRow(row.id, e.target.checked)}
        />
      </TableCell>

      {/* Hora */}
      <TableCell>{renderHora()}</TableCell>

      {/* #Evento */}
      <TableCell>{renderEvento()}</TableCell>

      {/* Prueba => row.prueba + row.testType */}
      <TableCell>
        {row.prueba} {row.testType}
      </TableCell>

      {/* Categoría */}
      <TableCell>{row.categoria}</TableCell>

      {/* Rama */}
      <TableCell>{row.rama}</TableCell>

      {/* Especificaciones (Esp) */}
      <TableCell>{renderEspecificaciones()}</TableCell>

      {/* Fase */}
      <TableCell>{renderFase()}</TableCell>

      {/* Heats (no editable) */}
      <TableCell>{row.heats}</TableCell>

      {/* Inscritos (no editable) */}
      <TableCell>{row.inscritos}</TableCell>

      {/* Acciones */}
      <TableCell>
        {/* Si estamos en edición masiva, no se permite edición individual */}
        {!massEditing && (
          <>
            {editingId === row.id ? (
              <>
                <IconButton onClick={() => handleSaveRow(row.id)}>
                  <SaveIcon />
                </IconButton>
                <IconButton onClick={handleCancelEditRow}>
                  {/* Ícono de cancelar (puedes cambiarlo por uno más apropiado) */}
                  <DeleteIcon />
                </IconButton>
              </>
            ) : (
              <>
                <IconButton onClick={() => handleEditRow(row)}>
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => onDeleteOne(row)}>
                  <DeleteIcon />
                </IconButton>
              </>
            )}
          </>
        )}
      </TableCell>
    </TableRow>
  );
}

export default SessionSchedules;
